import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Images, ImageOff, MessageSquare, Check, Loader2 } from 'lucide-react';
import {
  xamplinApi,
  PURPOSE_BY_ID,
  type XpPhoto,
  type PhotoTag,
  type PhotoReaction,
} from '@/lib/api/xamplin';
import { queryKeys } from '@/lib/api/queryKeys';
import { cn } from '@/lib/cn';
import { toast } from '@/components/ui/Toast';
import { HelpHint } from '@/features/help/HelpHint';
import { usePagination } from '@/lib/hooks/usePagination';
import { Pagination } from '@/components/ui/Pagination';

/**
 * Photos (build-v2 F9) — social layer over field captures: reactions, comments,
 * recap/great-work/needs-attention tags and approval. Mirrors the prototype
 * `photos.jsx` PhotoCard. Company-scoped via the backend.
 */
const TAG_META: Record<PhotoTag, { label: string; color: string; icon: string }> = {
  'add-to-recap': { label: 'Recap', color: '#5aa9ff', icon: '✦' },
  'great-work': { label: 'Great work', color: '#4dd28b', icon: '👊' },
  'needs-attention': { label: 'Needs attention', color: '#f4b740', icon: '⚠' },
};
const REACTIONS: { id: PhotoReaction; icon: string }[] = [
  { id: 'fistbump', icon: '👊' },
  { id: 'heart', icon: '❤️' },
  { id: 'fire', icon: '🔥' },
];
const PURPOSE_COLOR: Record<string, string> = {
  SEE: '#5aa9ff',
  ENGAGE: '#f4b740',
  TRY: '#4dd28b',
  ACT: '#ff5a4f',
};
type FilterKey = 'all' | PhotoTag | 'approved';
const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'add-to-recap', label: 'Recap' },
  { key: 'great-work', label: 'Great work' },
  { key: 'needs-attention', label: 'Needs attention' },
  { key: 'approved', label: 'Approved' },
];

type ViewRole = 'admin' | 'manager' | 'reporter';

export function PhotosPage() {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [role, setRole] = useState<ViewRole>('admin');
  const canManage = role === 'admin' || role === 'manager';

  const params = useMemo(() => {
    if (filter === 'all') return { limit: 200 };
    if (filter === 'approved') return { approved: true, limit: 200 };
    return { tag: filter, limit: 200 };
  }, [filter]);

  const feedQ = useQuery({
    queryKey: queryKeys.xamplin.photoFeed(params),
    queryFn: () => xamplinApi.photos.feed(params),
  });
  const photos = feedQ.data?.items ?? [];
  const pg = usePagination(photos, 24);

  return (
    <div className="page flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--color-accent-10)] text-[var(--color-xk-accent)]">
          <Images className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[var(--color-xk-lavender)]">
            Photos
          </h1>
          <p className="mono text-xs uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            {photos.length} fotos · social feed
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {/* Role toggle (view-as) — preview as Manager/Reporter (G12) */}
          <div className="flex items-center gap-1 rounded-lg border border-[var(--color-xk-border)] p-0.5">
            <span className="mono px-1.5 text-[9px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
              View as
            </span>
            {(['admin', 'manager', 'reporter'] as ViewRole[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRole(r)}
                className={cn(
                  'mono rounded-md px-2 py-1 text-[10px] font-bold capitalize transition-colors',
                  role === r ? 'bg-[var(--color-xk-accent)] text-white' : 'text-[var(--color-xk-text-muted)]',
                )}
              >
                {r}
              </button>
            ))}
          </div>
          <HelpHint section="photos" />
        </div>
      </div>

      {!canManage && (
        <div className="mono rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-4 py-2 text-[11px] text-[var(--color-xk-text-muted)]">
          👀 Vista de Reporter — solo lectura + reacciones. Tags y aprobación son de Manager/Admin.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                'rounded-full border px-3 py-1.5 text-xs font-bold transition-colors',
                active
                  ? 'border-[var(--color-xk-accent)] bg-[var(--color-xk-accent)] text-white'
                  : 'border-[var(--color-xk-border)] text-[var(--color-xk-text-muted)] hover:bg-[var(--color-xk-surface)]',
              )}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {feedQ.isLoading && (
        <div className="mono flex items-center gap-2 text-sm text-[var(--color-xk-text-muted)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando fotos…
        </div>
      )}
      {feedQ.isError && (
        <div className="rounded-xl border border-[var(--color-error)] bg-[var(--color-error)]/10 px-4 py-3 text-sm text-[var(--color-error)]">
          No se pudieron cargar las fotos.
        </div>
      )}
      {!feedQ.isLoading && !feedQ.isError && photos.length === 0 && (
        <div className="mono text-sm text-[var(--color-xk-text-muted)]">No hay fotos para este filtro.</div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {pg.pageItems.map((p) => (
          <PhotoCard key={p.id} photo={p} filterKey={params} canManage={canManage} />
        ))}
      </div>
      <Pagination {...pg} />
    </div>
  );
}

function PhotoCard({ photo, filterKey, canManage }: { photo: XpPhoto; filterKey: Record<string, unknown>; canManage: boolean }) {
  const qc = useQueryClient();
  const [showComments, setShowComments] = useState(false);
  const [draft, setDraft] = useState('');
  const purpose = photo.purposeId ? PURPOSE_BY_ID[photo.purposeId] : null;

  const invalidateFeed = () =>
    qc.invalidateQueries({ queryKey: queryKeys.xamplin.photoFeed(filterKey) });

  const reactM = useMutation({
    mutationFn: (r: PhotoReaction) => xamplinApi.photos.react(photo.id, r),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.xamplin.photoReactions(photo.id) });
      invalidateFeed();
    },
    onError: () => toast({ tone: 'error', title: 'No se pudo reaccionar' }),
  });

  const tagM = useMutation({
    mutationFn: (t: PhotoTag) => xamplinApi.photos.toggleTag(photo.id, t),
    onSuccess: () => invalidateFeed(),
    onError: () => toast({ tone: 'error', title: 'No se pudo etiquetar' }),
  });

  const approveM = useMutation({
    mutationFn: () => xamplinApi.photos.setApproval(photo.id, !photo.approved),
    onSuccess: () => {
      toast({ tone: 'success', title: photo.approved ? 'Aprobación retirada' : 'Foto aprobada' });
      invalidateFeed();
    },
    onError: () => toast({ tone: 'error', title: 'No se pudo aprobar' }),
  });

  const commentsQ = useQuery({
    queryKey: queryKeys.xamplin.photoComments(photo.id),
    queryFn: () => xamplinApi.photos.comments(photo.id),
    enabled: showComments,
  });

  const commentM = useMutation({
    mutationFn: (body: string) => xamplinApi.photos.comment(photo.id, body),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: queryKeys.xamplin.photoComments(photo.id) });
      invalidateFeed();
    },
    onError: () => toast({ tone: 'error', title: 'No se pudo comentar' }),
  });

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)]">
      <div className="relative aspect-[4/3] w-full bg-[var(--color-xk-bg)]">
        {photo.image ? (
          <img src={photo.image} alt={photo.description ?? 'foto'} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center text-[var(--color-xk-text-subtle)]">
            <ImageOff className="h-6 w-6" />
          </div>
        )}
        {purpose && (
          <span
            className="mono absolute left-2 top-2 rounded-full px-2 py-0.5 text-[9px] font-bold text-white"
            style={{ background: PURPOSE_COLOR[purpose] }}
          >
            {purpose}
          </span>
        )}
        {photo.approved && (
          <span className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-[#4dd28b] text-white">
            <Check className="h-3.5 w-3.5" />
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2.5 p-3">
        {(photo.event || photo.group) && (
          <div className="min-w-0">
            <div className="truncate text-sm font-bold text-[var(--color-xk-lavender)]">
              {photo.event?.name ?? '—'}
            </div>
            {photo.group && (
              <div className="mono truncate text-[10px] uppercase tracking-wider text-[var(--color-xk-text-muted)]">
                {photo.group.name}
              </div>
            )}
          </div>
        )}

        {/* Reactions */}
        <div className="flex items-center gap-1.5">
          {REACTIONS.map((r) => (
            <button
              key={r.id}
              type="button"
              disabled={reactM.isPending}
              onClick={() => reactM.mutate(r.id)}
              className="grid h-7 w-7 place-items-center rounded-full border border-[var(--color-xk-border)] text-sm transition-transform hover:scale-110 disabled:opacity-50"
              title={r.id}
            >
              {r.icon}
            </button>
          ))}
          {photo.reactions > 0 && (
            <span className="mono text-[11px] text-[var(--color-xk-text-muted)]">{photo.reactions}</span>
          )}
          <button
            type="button"
            onClick={() => setShowComments((s) => !s)}
            className="ml-auto flex items-center gap-1 text-[11px] text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
          >
            <MessageSquare className="h-3.5 w-3.5" /> {photo.comments}
          </button>
        </div>

        {/* Tags + approval — Manager/Admin only (reporter view is read-only) */}
        {canManage && (
        <div className="flex flex-wrap gap-1.5">
          {(Object.keys(TAG_META) as PhotoTag[]).map((t) => {
            const on = photo.tags.includes(t);
            const meta = TAG_META[t];
            return (
              <button
                key={t}
                type="button"
                disabled={tagM.isPending}
                onClick={() => tagM.mutate(t)}
                className={cn(
                  'mono rounded-full border px-2 py-0.5 text-[9px] font-bold transition-colors disabled:opacity-50',
                  on ? 'text-white' : 'text-[var(--color-xk-text-muted)]',
                )}
                style={on ? { background: meta.color, borderColor: meta.color } : { borderColor: 'var(--color-xk-border)' }}
              >
                {meta.icon} {meta.label}
              </button>
            );
          })}
          <button
            type="button"
            disabled={approveM.isPending}
            onClick={() => approveM.mutate()}
            className={cn(
              'mono ml-auto rounded-full border px-2 py-0.5 text-[9px] font-bold transition-colors disabled:opacity-50',
              photo.approved ? 'border-[#4dd28b] bg-[#4dd28b] text-white' : 'border-[var(--color-xk-border)] text-[var(--color-xk-text-muted)]',
            )}
          >
            {photo.approved ? 'Aprobada' : 'Aprobar'}
          </button>
        </div>
        )}

        {/* Comments */}
        {showComments && (
          <div className="flex flex-col gap-2 border-t border-[var(--color-xk-border)] pt-2">
            {commentsQ.isLoading && (
              <span className="mono text-[11px] text-[var(--color-xk-text-muted)]">Cargando…</span>
            )}
            {(commentsQ.data ?? []).map((c) => (
              <div key={c.id} className="text-[12px]">
                <span className="font-bold text-[var(--color-xk-lavender)]">{c.authorName ?? 'Anónimo'}</span>{' '}
                <span className="text-[var(--color-xk-text-muted)]">{c.body}</span>
              </div>
            ))}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (draft.trim()) commentM.mutate(draft.trim());
              }}
              className="flex gap-2"
            >
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Comentar…"
                className="min-w-0 flex-1 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-2 py-1 text-[12px] text-[var(--color-xk-lavender)] outline-none focus:border-[var(--color-xk-accent)]"
              />
              <button
                type="submit"
                disabled={commentM.isPending || !draft.trim()}
                className="rounded-lg bg-[var(--color-xk-accent)] px-2.5 py-1 text-[11px] font-bold text-white disabled:opacity-50"
              >
                Enviar
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
